// Merchant feeds deliver HTML-entity-encoded text ("The Book Lover&#8217;s
// Reading Challenge") and the pipeline stores it verbatim, so any surface that
// renders a raw or frozen-snapshot string shows the literal entity. Decoding
// belongs at the display seams (adapters/hooks), because frozen copies —
// carouselSessions, giftActivity productSnapshots, rail snapshots — keep old
// strings forever and can't be repaired by catalog writes alone.
//
// Deliberately DOM-free (runs in jsdom tests) and single-pass: one scan, no
// re-decoding of the output, so a double-encoded "&amp;#39;" correctly becomes
// "&#39;" and stops — matching HTML semantics. Only entities with a trailing
// ";" are touched; a bare "&" in plain text ("Crate & Barrel") never is.

const NAMED: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  rsquo: '’',
  lsquo: '‘',
  rdquo: '”',
  ldquo: '“',
  mdash: '—',
  ndash: '–',
  hellip: '…',
  deg: '°',
  copy: '©',
  reg: '®',
  trade: '™',
  eacute: 'é',
};

const ENTITY_RE = /&(#\d+|#[xX][0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]{1,30});/g;

// Zero-width characters (&#8203; etc.) are invisible junk in a title; nbsp
// forces odd wrapping — normalize both away after decoding.
const ZERO_WIDTH_RE = /[\u200B\u200C\u200D\uFEFF]/g;

function decodeOne(entity: string): string {
  if (entity[0] === '#') {
    const code =
      entity[1] === 'x' || entity[1] === 'X'
        ? parseInt(entity.slice(2), 16)
        : parseInt(entity.slice(1), 10);
    // Whitespace control codes are REAL in merchant feeds (&#13; appears in
    // 28 live descriptions) — decode them so cleanProductDescription's
    // line-break split sees true structure. Reject other control chars and
    // anything outside Unicode's range — keep the literal text rather than
    // emitting garbage.
    if (code === 10 || code === 13) return '\n';
    if (code === 9) return ' ';
    if (!Number.isFinite(code) || code < 32 || code > 0x10ffff) return `&${entity};`;
    return String.fromCodePoint(code);
  }
  const named = NAMED[entity];
  return named !== undefined ? named : `&${entity};`;
}

export function decodeHtmlEntities(s: string): string {
  if (!s || s.indexOf('&') === -1) return s;
  return s
    .replace(ENTITY_RE, (_, entity: string) => decodeOne(entity))
    .replace(ZERO_WIDTH_RE, '')
    .replace(/\u00A0/g, ' ')
    // Collapse runs of spaces/tabs but PRESERVE line breaks --
    // cleanProductDescription splits on them after decoding.
    .replace(/[^\S\r\n]{2,}/g, ' ')
    .trim();
}

/** Same decode for optional fields (brand etc.) without disturbing undefined. */
export function decodeHtmlEntitiesOpt(s: string | undefined): string | undefined {
  return s === undefined ? undefined : decodeHtmlEntities(s);
}
